import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementCard, AiManagementState } from 'store/modules/ai_management_store/types';
import { getColumns } from 'store/modules/ai_management_store/actions';
import { listCards } from 'services/actions/ai_management_cards';

export type AiManagementCardModalAction = 'new_task' | 'new_update' | 'updates';

export interface AiManagementCardLookup {
  cardId?: number;
  biddingId?: number;
}

const matches = (lookup: AiManagementCardLookup) => (card: AiManagementCard) =>
  lookup.cardId !== undefined ? card.id === lookup.cardId : card.bidding_id === lookup.biddingId;

const flatten = (grouped: AiManagementState['cards'] | undefined) =>
  Object.values(grouped ?? {}).flat();

export function useAiManagementCardModal() {
  const dispatch = useDispatch();
  const boardCards = useSelector<ApplicationState, AiManagementState['cards']>(
    (state) => state.session.ai_management.cards,
  );
  const [card, setCard] = useState<AiManagementCard | null>(null);
  const [action, setAction] = useState<AiManagementCardModalAction | undefined>(undefined);
  const [opening, setOpening] = useState<AiManagementCardLookup | null>(null);

  const openCard = (
    lookup: AiManagementCardLookup,
    nextAction?: AiManagementCardModalAction,
  ): Promise<AiManagementCard | null> => {
    dispatch(getColumns());
    setAction(nextAction);

    const known = flatten(boardCards).find(matches(lookup));
    if (known) {
      setCard(known);
      return Promise.resolve(known);
    }

    setOpening(lookup);
    return listCards()
      .then((response) => {
        if (!response || response.status >= 400) return null;
        const found = flatten(response.data?.cards).find(matches(lookup)) ?? null;
        setCard(found);
        return found;
      })
      .catch(() => null)
      .finally(() => setOpening(null));
  };

  const close = () => setCard(null);

  return { card, action, opening, openCard, close };
}
